import { FC, memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { VideoCameraAddOutlined } from '@ant-design/icons';
import { createCreatorAppointment } from 'services/api/appointments';

import { showRestError } from 'utils/showErrorMessage';
import { CreateItem } from 'components/shared';
import CreateVideoCallModal, {
  CreateVideoCallModalRef
} from '../CreateVideoCallModal';

interface IProps {
  onCreate(): void;
}

const CreateVideoCall: FC<IProps> = ({ onCreate }) => {
  const { t } = useTranslation();
  const modalRef = useRef<CreateVideoCallModalRef>(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const onClose = () => {
    setModalVisible(false);
  };

  const onCreateAppointment = (notes: string) => {
    setIsCreating(true);
    // POST /appointments/creator {notes} → created session row; the BE
    // returns hashId (encrypted id) which keys the meeting room + share URL.
    createCreatorAppointment(notes)
      .then(row => {
        const meetingId = row?.hashId ?? row?.id;

        if (meetingId) {
          modalRef.current?.onAppointmentCreated(String(meetingId));
        }
      })
      .catch(err => showRestError(err))
      .finally(() => setIsCreating(false));
  };

  return (
    <>
      <CreateItem title={t('video_call.create_new')}>
        <Button
          size="large"
          type="primary"
          loading={isCreating}
          icon={<VideoCameraAddOutlined />}
          onClick={() => setModalVisible(true)}
        >
          {t('video_call.create_new')}
        </Button>
      </CreateItem>
      {isModalVisible && (
        <CreateVideoCallModal
          ref={modalRef}
          onClose={onClose}
          onCreateAppointment={onCreateAppointment}
          onCreate={onCreate}
        />
      )}
    </>
  );
};

export default memo(CreateVideoCall);
